import 'https://cdn.venturz.co/lib/aug14.min.js';

import cookie from '../cookie.js';

import PageViewObserver from './page-view.js';

class UserObserver {

  constructor(observeUser = true, { observerIdCookieKey = 'ht-uoid', siteSessionIdKey = 'site-sessionid', isPlatform = false, origin = null, dryRun = false } = { observerIdCookieKey: 'ht-uoid', siteSessionIdKey: 'site-sessionid', isPlatform: false, origin: null }) {

    this.observerIdCookieKey = observerIdCookieKey;
    this.siteSessionIdKey = siteSessionIdKey;
    this.isPlatform = isPlatform;
    this.siteSessionId = null;
    this.pageSessionId = null;
    this.observeUser = observeUser;
    this.pageViewObserver = null;
    this.displayObserver = null;
    this.pageViewStatusTimerReference = null;
    this.userType = null;
    this.firstTime = false;
    this.origin = this.determineOrigin(origin);
    this.url = new URL(window.location.href);
    this.initializingPromiseResolver = null;
    this.initializingPromise = new Promise(function resolver(resolve) {
      this.initializingPromiseResolver = resolve;
    }.bind(this));
    this.sessionPath = this.getSessionPath(this.isPlatform);
    this.eventsPath = this.getEventsPath(this.isPlatform);
    this.displayPath = this.getDisplayPath(this.isPlatform);
    this.pageStatusPath = this.getPageStatusPath(this.isPlatform);
    this.scriptPromises = {};
    this.dryRun = dryRun;

    window.obs = this;

  }

  determineOrigin(origin) {

    if (origin !== null) {
      return origin;
    }

    return window.location.origin;

  }

  getAPIBasePath() {

    if (window.location.hostname.indexOf('localhost') !== -1) {
      return 'http://localhost:3000/v1';
    }

    return 'https://api.venturz.co/v1';

  }

  getSessionPath(isPlatform) {

    if (isPlatform === true) {
      return this.origin + '/_/initialize-platform-session';
    }

    return this.origin + '/_/initialize';

  }

  getEventsPath(isPlatform) {

    if (isPlatform === true) {
      return this.origin + '/_/platform-events';
    }

    return this.origin + '/_/events';

  }

  getDisplayPath(isPlatform) {

    if (isPlatform === true) {
      return this.origin + '/_/platform-display';
    }

    return this.origin + '/_/display';

  }

  getPageStatusPath(isPlatform) {

    if (isPlatform === true) {
      return this.origin + '/_/platform-page-status';
    }

    return this.origin + '/_/page-status';

  }

  getObserverId() {

    let observerId = cookie.get(this.observerIdCookieKey) || window['__ht-uoid'] || null; //window['__ht-uoid'] fallback for iframe access from assistant

    return observerId;

  }

  getSiteSessionId() {

    if (this.pageViewObserver === null) {
      return null;
    }

    return this.pageViewObserver.getSiteSessionId();

  }

  async restart() {

    await this.stop();
    sessionStorage.removeItem('page-session-id');
    await this.start();

  }

  setSiteSessionId(siteSessionId) {

    if (this.pageViewObserver === null) {
      throw Error('Cant set session id: PageViewObserver is not initialized');
    }

    this.pageViewObserver.setSiteSessionId(siteSessionId);

  }

  setObserverId(observerId) {

    if (this.getObserverId() === observerId) {
      return;
    }

    this.pageViewObserver.sessionId = null;

    this.setObserverIdCookie(observerId);
    return this.restart();

  }

  setObserverIdCookie(observerId) {

    cookie.set(this.observerIdCookieKey, observerId, {
      expires: 1000,
      path: '/'
    });

  }

  tryReinitializeSession(siteSessionId) {

    if (this.getSiteSessionId() === null) {
      this.setSiteSessionId(siteSessionId);
      return;
    }

    if (this.getSiteSessionId() === siteSessionId) {
      return;
    }

    this.stop();

    this.doStart(siteSessionId);

  }

  getInitializeSessionPayload() {

    const observerId = this.getObserverId();
    const sessionId = this.getSiteSessionId();
    const authorizationToken = cookie.get('authorizationToken');

    return { observerId, sessionId, authorizationToken };

  }

  async loadSession() {

    const sessionId = sessionStorage.getItem('page-session-id') || null;

    if (sessionId !== null) {
      const result = {
        _id: sessionStorage.getItem('page-session-id'),
        observerId: this.getObserverId()
      };

      this.userType = cookie.get('ut') === 'r' ? 'RETURNING' : 'NEW';

      return result;
    }

    const payload = this.getInitializeSessionPayload();

    const url = this.sessionPath;

    const response = await fetch(url, { method: 'POST', body: JSON.stringify(payload) });

    const result = await response.json();

    this.userType = result.observerId === payload.observerId ? 'RETURNING' : 'NEW';

    this.setObserverIdCookie(result.observerId);

    return result;

  }

  async emitPageViewEvent() {

    const payload = this.buildPageViewEventPayload();

    const result = await this.emitPageEvent(payload);

    this.pageSessionId = result.pageSessionId;

  }

  async emitMessageSentEvent(metadata) {

    await this.pageViewObserver.emit('Message Sent', metadata);

  }

  async emitMessageEvent(metadata) {

    await this.pageViewObserver.emit('Messages', metadata);

  }

  async emitStartupCreatedEvent(metadata) {

    await this.pageViewObserver.emit('Startup Created', metadata);

  }

  loadScript(url) {

    let promise = this.scriptPromises[url] || null;

    if (promise !== null) {
      return promise;
    }

    this.scriptPromises[url] = new Promise(function resolver(resolve, reject) {

      const script = document.createElement('script');

      script.src = url;

      script.onload = function() {
        resolve();
      };

      script.onerror = function() {
        reject('Failed to load script', url);
      };

      document.head.appendChild(script);

    });

    return this.scriptPromises[url];

  }

  deleteQueryParameters(names) {

    const url = new URL(window.location.href);

    for (let name of names) {
      url.searchParams.delete(name);
      this.url.searchParams.delete(name);
    }

    window.history.replaceState({}, '', url);

  }

  async tryEmitSignupEvent(data) {

    if (data.registered === false) {
      return;
    }

    const source = data.provider;

    await this.pageViewObserver.emitSignupEvent(source);

  }

  async tryEmitSignupEventUsingURL() {

    const loginModeKey = '__login_mode__';
    const loginSourceKey = '__login_source__';

    const status = this.url.searchParams.get(loginModeKey);
    const source = this.url.searchParams.get(loginSourceKey);

    this.deleteQueryParameters([loginModeKey, loginSourceKey]);

    if (status !== 'SIGNUP') {
      return;
    }

    await this.pageViewObserver.emitSignupEvent(source);

  }

  async tryEmitPaidUserEvent() {

    const productKey = '__product__';

    const exists = this.url.searchParams.has(productKey);

    if (exists === false) {
      return;
    }

    const product = this.url.searchParams.get(productKey);

    await this.pageViewObserver.emitPaidUserEvent(product);

    this.deleteQueryParameters([productKey]);

  }

  async getMinifiedObject(dataId) {

    const url = this.getAPIBasePath() + '/general/minified-objects/' + dataId;

    const response = await fetch(url);

    const data = await response.json();

    return data.data;

  }

  async tryEmitMessageEvent() {

    const key = '__evt__';
    const dataIdKey = '__data-id__';
    const buttonKey = '__btn__';

    const exists = this.url.searchParams.has(key);

    if (exists === false) {
      return;
    }

    const eventType = this.url.searchParams.get(key);

    if (eventType !== 'Message') {
      return;
    }

    const dataId = this.url.searchParams.get(dataIdKey);
    const button = this.url.searchParams.get(buttonKey);

    this.deleteQueryParameters([key, dataIdKey, buttonKey, 'src']);

    const data = await this.getMinifiedObject(dataId);

    if (data === null) {
      return;
    }

    this.emitMessageEvent({
      Origin: data.origin,
      Subject: data.subject,
      Action: button,
      Status: 'Clicked',
      Receiver: data.receiverId
    });

  }

  async tryEmitSecondaryEvents() {

    await this.tryEmitPaidUserEvent();
    await this.tryEmitMessageEvent();

  }

  /**
   * 
   * This must 
   * 1. Emit page view event
   * 2. Start recording page
   * 3. Start observing clicks
   * 4. Start reporting page view status
   * */

  async doStart(sessionId) {

    this.pageViewObserver = new PageViewObserver(this, sessionId);

    await this.pageViewObserver.start();

  }

  async start() {

    await this.loadScript('https://cdn.venturz.co/lib/aug14.min.js');

    const session = await this.loadSession();

    await this.doStart(session._id);

    if (this.firstTime === false) {
      this.firstTime = true;
      await this.tryEmitSecondaryEvents();
    }

  }

  stop() {

    this.pageViewObserver.stop();

  }

}

export default UserObserver;